/* eslint-disable no-console */
import { useEffect, useState } from 'react';
import { LiveComponent } from '@amzn/aws-tc-xb-xps';
import { ContentLayout } from '@amzn/awsui-components-react';
import { PostAuthComponentParams } from '../../types/auth';
import { AppConfig } from '../../types/app';
import { useApolloClient } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useIntl } from 'react-intl';
import { emitPostAuthRumEvent } from '../../services/emitPostAuthRumEvent';
import { PostAuthRumEvent } from '../../constants/metrics';
import GandalfFlashbar from '../GandalfFlashbar';

const PROFILE_STATUS = {
  COMPLETE: 'COMPLETE',
  SIMPLE: 'SIMPLE',
  MINIMAL: 'MINIMAL',
  INCOMPLETE: 'INCOMPLETE',
};

const COMPONENT_NAME = {
  COMPLETE: 'UserProfileEditAll',
  SIMPLE: 'SimplifiedProfile',
};

export const getAuthLink = (jwtToken: string) => {
  return setContext((_, { headers }) => {
    console.log('Setting auth header');
    console.log(headers);
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${jwtToken}`,
      },
    };
  });
};

function ProfileAndPreferences({
  config,
  onContinue,
  user,
}: PostAuthComponentParams) {
  const apolloClient = useApolloClient();
  const { locale } = useIntl();
  const [componentToRender, setComponentToRender] = useState(
    COMPONENT_NAME.COMPLETE
  );

  useEffect(() => {
    const listener = () => {
      emitPostAuthRumEvent(user, PostAuthRumEvent.PROFILE_COMPLETE);
      onContinue();
    };
    document.addEventListener('edit_profile_all_sections_success', listener);
    return () => {
      document.removeEventListener(
        'edit_profile_all_sections_success',
        listener
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const requiredStatus = getClientRequiredStatus(config);

    if (requiredStatus === PROFILE_STATUS.SIMPLE) {
      if (user.simplified_profile_completed === 'true') {
        onContinue();
        return;
      }
    } else if (requiredStatus === PROFILE_STATUS.COMPLETE) {
      if (user.profile_completion_status === PROFILE_STATUS.COMPLETE) {
        onContinue();
        return;
      }
    } else {
      //requiredStatus does not match neither SIMPLE nor COMPLETE
      onContinue();
      return;
    }

    setComponentToRender(
      requiredStatus === PROFILE_STATUS.SIMPLE
        ? COMPONENT_NAME.SIMPLE
        : COMPONENT_NAME.COMPLETE
    );

    const postAuthRumEvent =
      requiredStatus === PROFILE_STATUS.SIMPLE
        ? PostAuthRumEvent.SIMPLE_PROFILE_RENDERED
        : PostAuthRumEvent.COMPLETE_PROFILE_RENDERED;
    emitPostAuthRumEvent(user, postAuthRumEvent);
  }, [config, onContinue, user, user.profile_completion_status]);

  apolloClient.setLink(getAuthLink(user.jwtToken!).concat(apolloClient.link));

  return (
    <ContentLayout>
      <GandalfFlashbar />
      <LiveComponent
        experienceProviderName="userorg"
        componentName={componentToRender}
        payload={JSON.stringify({
          token: user?.jwtToken,
          language: locale,
        })}
        data-testid="profile-live-component"
      />
    </ContentLayout>
  );
}

const getClientRequiredStatus = (config: AppConfig): string | undefined => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const clientId = urlSearchParams.get('client_id');
  return config.requiredProfileCompletionStatusForClients?.[clientId!];
};

export default ProfileAndPreferences;
